import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Building2, Plus, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { apiErrorText } from '../../utils/apiErrorText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtIban = (s) => (s || '').replace(/(.{4})/g, '$1 ').trim();

const Editor = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-3" data-testid="supplier-account-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label className="text-xs">{t('supplierAccounts.name')}</Label><Input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid="supplier-account-name" /></div>
        <div><Label className="text-xs">NIP</Label><Input value={value.nip || ''} disabled={!!value.id} onChange={(e) => onChange({ ...value, nip: e.target.value })} data-testid="supplier-account-nip" /></div>
      </div>
      <div><Label className="text-xs">{t('supplierAccounts.accounts')}</Label>
        <Textarea rows={4} value={value.accounts} onChange={(e) => onChange({ ...value, accounts: e.target.value })} placeholder={t('supplierAccounts.accountsPh')} data-testid="supplier-account-ibans" />
      </div>
    </div>
  );
};

/** Supplier bank accounts used to match purchase invoices even without an invoice number in the transfer title. */
export const SupplierAccountsDialog = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  const load = () => axios.get(`${API}/bank/supplier-accounts`).then((r) => setRows(r.data)).catch((e) => toast.error(apiErrorText(e, t, 'bank.failed')));
  useEffect(() => { if (open) load(); }, [open]);
  const save = async () => {
    const body = { name: edit.name, nip: edit.nip || null, accounts: edit.accounts.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean) };
    try {
      if (edit.id) await axios.put(`${API}/bank/supplier-accounts/${edit.id}`, body);
      else await axios.post(`${API}/bank/supplier-accounts`, body);
      toast.success(t('supplierAccounts.saved')); setEdit(null); load();
    } catch (e) { toast.error(apiErrorText(e, t, 'bank.failed')); }
  };
  const shown = rows.filter((r) => !q || `${r.name} ${r.nip} ${(r.accounts || []).map((a) => a.iban).join(' ')}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <Button size="sm" variant="outline" className="h-8" onClick={() => setOpen(true)} data-testid="supplier-accounts-open"><Building2 className="h-4 w-4 mr-1" />{t('supplierAccounts.button')}</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEdit(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="supplier-accounts-dialog">
          <DialogHeader><DialogTitle>{t('supplierAccounts.title')}</DialogTitle><DialogDescription>{t('supplierAccounts.hint')}</DialogDescription></DialogHeader>
          {edit ? <Editor value={edit} onChange={setEdit} /> : (
            <div className="space-y-2">
              <Input className="h-8" placeholder={t('supplierAccounts.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="supplier-accounts-search" />
              {!shown.length && <p className="text-sm text-slate-500">{t('supplierAccounts.empty')}</p>}
              {shown.map((r) => (
                <div key={r.id} className="flex items-start gap-2 rounded border p-2 text-sm" data-testid={`supplier-account-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{r.name || '—'} {r.nip && <span className="text-xs text-slate-500">· NIP {r.nip}</span>}</div>
                    {(r.accounts || []).map((a) => (
                      <div key={a.iban} className="flex flex-wrap items-center gap-1 text-xs font-mono text-slate-600">{fmtIban(a.iban)}
                        <Badge variant="outline" className="text-[10px] font-sans">{t(`supplierAccounts.src.${a.source}`)}</Badge></div>
                    ))}
                    {!(r.accounts || []).length && <div className="text-xs text-slate-400">{t('supplierAccounts.none')}</div>}
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit({ ...r, accounts: (r.accounts || []).map((a) => a.iban).join('\n') })} data-testid={`supplier-account-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="gap-2">
            {edit ? <>
              <Button variant="outline" onClick={() => setEdit(null)} data-testid="supplier-account-cancel">{t('common.cancel')}</Button>
              <Button onClick={save} data-testid="supplier-account-save">{t('common.save')}</Button>
            </> : <Button onClick={() => setEdit({ name: '', nip: '', accounts: '' })} data-testid="supplier-account-add"><Plus className="h-4 w-4 mr-1" />{t('supplierAccounts.add')}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupplierAccountsDialog;
